import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Platform,
  Alert,
  Switch,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import DateTimePicker from '@react-native-community/datetimepicker';
import { colors } from '../constants/colors';
import { TimeSlot, SUBJECT_COLORS } from '../types/timetable';
import { scheduleClassNotifications } from '../services/notifications';

interface ClassEditModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (classData: TimeSlot) => void;
  initialData?: TimeSlot;
  existingClasses: TimeSlot[];
  selectedDay: string;
}

const ClassEditModal: React.FC<ClassEditModalProps> = ({
  visible,
  onClose,
  onSave,
  initialData,
  existingClasses,
  selectedDay,
}) => {
  const [subject, setSubject] = useState('');
  const [location, setLocation] = useState('');
  const [lecturer, setLecturer] = useState('');
  const [startTime, setStartTime] = useState(new Date());
  const [endTime, setEndTime] = useState(new Date());
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);
  const [selectedColor, setSelectedColor] = useState(SUBJECT_COLORS[0]);
  const [notifyBeforeStart, setNotifyBeforeStart] = useState(true);
  const [notifyBeforeEnd, setNotifyBeforeEnd] = useState(true);
  const [previousStartTime, setPreviousStartTime] = useState<Date | null>(null);

  useEffect(() => {
    if (initialData) {
      setSubject(initialData.subject);
      setLocation(initialData.location || '');
      setLecturer(initialData.lecturer || '');
      setSelectedColor(initialData.color || SUBJECT_COLORS[0]);
      setNotifyBeforeStart(initialData.notifications?.beforeStart ?? true);
      setNotifyBeforeEnd(initialData.notifications?.beforeEnd ?? true);

      const [startHour, startMinute] = initialData.time.split(' - ')[0].split(':');
      const [endHour, endMinute] = initialData.time.split(' - ')[1].split(':');
      const newStartTime = new Date();
      newStartTime.setHours(parseInt(startHour), parseInt(startMinute));
      const newEndTime = new Date();
      newEndTime.setHours(parseInt(endHour), parseInt(endMinute));
      setStartTime(newStartTime);
      setEndTime(newEndTime);
    } else {
      resetForm();
    }
  }, [initialData]);

  useEffect(() => {
    if (previousStartTime && initialData) {
      const oldTimeString = formatTime(previousStartTime);
      const newTimeString = formatTime(startTime);
      
      if (oldTimeString !== newTimeString) {
        console.log('Start time changed:', { 
          from: oldTimeString, 
          to: newTimeString 
        });
        
        // Create temporary class data to update notifications
        const updatedClass: TimeSlot = {
          id: initialData.id,
          subject: subject,
          location: location || undefined,
          lecturer: lecturer || undefined,
          time: `${formatTime(startTime)} - ${formatTime(endTime)}`,
          day: selectedDay,
          color: selectedColor,
          notifications: {
            beforeStart: notifyBeforeStart,
            beforeEnd: notifyBeforeEnd,
          },
        };

        // Update notifications immediately when time changes
        if (updatedClass.notifications?.beforeStart || updatedClass.notifications?.beforeEnd) {
          // Only update the notifications, don't save the class yet
          scheduleClassNotifications(updatedClass);
        }
      }
    }
    setPreviousStartTime(startTime);
  }, [startTime]);

  const resetForm = () => {
    setSubject('');
    setLocation('');
    setLecturer('');
    const defaultStart = new Date();
    defaultStart.setHours(8, 0);
    const defaultEnd = new Date();
    defaultEnd.setHours(9, 30);
    setStartTime(defaultStart);
    setEndTime(defaultEnd);
    setSelectedColor(SUBJECT_COLORS[0]);
    setNotifyBeforeStart(true);
    setNotifyBeforeEnd(true);
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const handleSave = async () => {
    if (!subject || !startTime || !endTime) {
      Alert.alert('Error', 'Please fill in all required fields');
      return;
    }

    const timeString = `${formatTime(startTime)} - ${formatTime(endTime)}`;
    const newClass: TimeSlot = {
      id: initialData?.id || Date.now().toString(),
      subject,
      location: location || undefined,
      lecturer: lecturer || undefined,
      time: timeString,
      day: selectedDay,
      color: selectedColor,
      notifications: {
        beforeStart: notifyBeforeStart,
        beforeEnd: notifyBeforeEnd,
      },
    };

    // Save all changes and close modal
    onSave(newClass);
    resetForm();
    onClose();
  };

  const timePickerStyles = Platform.select({
    ios: {
      backgroundColor: colors.background,
      borderRadius: 8,
    },
    android: {},
  });

  const renderTimePicker = (
    value: Date,
    onChange: (event: any, date?: Date) => void,
    show: boolean,
    setShow: (show: boolean) => void,
    label: string
  ) => {
    return (
      <View style={styles.timePickerContainer}>
        <Text style={styles.label}>{label}</Text>
        <TouchableOpacity
          style={[styles.timeButton, timePickerStyles]}
          onPress={() => setShow(true)}
        >
          <Text style={[styles.timeButtonText, !show && { color: colors.text }]}>
            {formatTime(value)}
          </Text>
          <Ionicons name="time-outline" size={20} color={colors.primary} />
        </TouchableOpacity>
        {(show || Platform.OS === 'ios') && (
          <DateTimePicker
            value={value}
            mode="time"
            is24Hour={true}
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={(event, date) => {
              if (Platform.OS === 'android') {
                setShow(false);
              }
              if (date) {
                onChange(event, date);
                // For iOS, we need to explicitly close the picker
                if (Platform.OS === 'ios') {
                  setShow(false);
                }
              }
            }}
            style={Platform.OS === 'ios' ? styles.iosTimePicker : undefined}
            textColor={colors.text}
            themeVariant="light"
          />
        )}
      </View>
    );
  };

  const renderColorPicker = () => (
    <View style={styles.colorPickerContainer}>
      <Text style={styles.label}>Color</Text>
      <View style={styles.colorGrid}>
        {SUBJECT_COLORS.map((color) => (
          <TouchableOpacity
            key={color}
            style={[
              styles.colorOption,
              { backgroundColor: color },
              selectedColor === color && styles.selectedColor,
            ]}
            onPress={() => setSelectedColor(color)}
          />
        ))}
      </View>
    </View>
  );

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={onClose}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalContent}>
          <View style={styles.header}>
            <Text style={styles.title}>
              {initialData ? 'Edit Class' : 'Add New Class'}
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.form}>
            <View style={styles.inputContainer}>
              <Text style={styles.label}>Subject *</Text>
              <TextInput
                style={styles.input}
                value={subject}
                onChangeText={setSubject}
                placeholder="Enter subject name"
                placeholderTextColor={colors.textSecondary}
              />
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>Location (Optional)</Text>
              <TextInput
                style={styles.input}
                value={location}
                onChangeText={setLocation}
                placeholder="Enter classroom/location"
                placeholderTextColor={colors.textSecondary}
              />
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>Lecturer (Optional)</Text>
              <TextInput
                style={styles.input}
                value={lecturer}
                onChangeText={setLecturer}
                placeholder="Enter lecturer name"
                placeholderTextColor={colors.textSecondary}
              />
            </View>

            {renderTimePicker(
              startTime,
              (_, date) => date && setStartTime(date),
              showStartPicker,
              setShowStartPicker,
              'Start Time *'
            )}

            {renderTimePicker(
              endTime,
              (_, date) => date && setEndTime(date),
              showEndPicker,
              setShowEndPicker,
              'End Time *'
            )}

            {renderColorPicker()}

            {/* <View style={styles.notificationContainer}>
              <Text style={styles.label}>Notifications</Text>
              <View style={styles.notificationOption}>
                <Text style={styles.notificationText}>
                  Notify 15 minutes before start
                </Text>
                <Switch
                  value={notifyBeforeStart}
                  onValueChange={setNotifyBeforeStart}
                  trackColor={{ false: colors.disabled, true: colors.primary }}
                />
              </View>

            </View> */}
          </ScrollView>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, styles.cancelButton]}
              onPress={onClose}
            >
              <Text style={styles.buttonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, styles.saveButton]}
              onPress={handleSave}
            >
              <Text style={[styles.buttonText, styles.saveButtonText]}>Save</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: colors.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 20,
    maxHeight: '90%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text,
  },
  closeButton: {
    padding: 8,
  },
  form: {
    paddingHorizontal: 20,
  },
  inputContainer: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    color: colors.textSecondary,
    marginBottom: 8,
  },
  input: {
    backgroundColor: colors.background,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: colors.text,
  },
  timePickerContainer: {
    marginBottom: 16,
  },
  timeButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    height: 48,
  },
  timeButtonText: {
    fontSize: 16,
    color: colors.text,
  },
  iosTimePicker: {
    backgroundColor: colors.background,
    width: '100%',
    height: 200,
    marginTop: 8,
    borderRadius: 8,
  },
  colorPickerContainer: {
    marginBottom: 16,
  },
  colorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 8,
  },
  colorOption: {
    width: 32,
    height: 32,
    borderRadius: 16,
    margin: 4,
  },
  selectedColor: {
    borderWidth: 2,
    borderColor: colors.text,
  },
  notificationContainer: {
    marginBottom: 16,
  },
  notificationOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  notificationText: {
    fontSize: 16,
    color: colors.text,
  },
  buttonContainer: {
    flexDirection: 'row',
    padding: 20,
    paddingTop: 0,
  },
  button: {
    flex: 1,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginHorizontal: 8,
  },
  cancelButton: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  saveButton: {
    backgroundColor: colors.primary,
  },
  buttonText: {
    fontSize: 16,
    color: colors.primary,
    fontWeight: '600',
  },
  saveButtonText: {
    color: colors.surface,
  },
});

export default ClassEditModal;
